import React, { ChangeEvent, useEffect, useState } from 'react';

import { Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import { getComparator, Order, stableSort } from 'utils/dataTableUtils';
import { makeStyles } from '@mui/styles';
import { ColumnDefinition, DataTableWithId } from 'components/Common/DataTable/DataTableTypes';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import EnhancedTableHead from 'components/Common/DataTable/EnhancedTableHead';
import { DataTableRow } from 'components/Common/DataTable/DataTableRow';

export const tableStyles = makeStyles(() => ({
  emptyTable: {
    borderBottom: 'none',
  },
}));

type Props<T> = {
  id?: any;
  headCells: ColumnDefinition<T>[];
  rows: T[];
  ariaLabel?: string;
  borders?: boolean;
  rowsPerPageOptions?: number[];
  onRowClick?: (rowId: string) => void;
  selectedRowId?: string;
  showPagination?: boolean;
  renderExpanded?: React.ReactNode;
  isLoading?: boolean;
  url?: string;
  actionButton?: React.ReactNode;
  emptyMessage?: string;
};

export const DataTable = <T extends DataTableWithId>({
  id,
  rows,
  ariaLabel,
  headCells,
  borders = true,
  rowsPerPageOptions = [],
  onRowClick = undefined,
  selectedRowId,
  showPagination = false,
  renderExpanded,
  isLoading,
  url,
  emptyMessage = 'No related partners found.',
}: Props<T>): React.ReactElement => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('label');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowsFiltered, setRowsFiltered] = useState<T[]>([]);
  const tableId = id;

  const classes = tableStyles();

  useEffect(() => {
    setRowsFiltered(rows);
  }, [JSON.stringify(rows)]);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (selectedFilters: { [key: string]: string }[]) => {
    const rowsMatchingFilters = selectedFilters
      ? rows?.filter(
          // TODO - use generic type

          (row: any) =>
            selectedFilters.some((selectedFilter) =>
              Object.keys(selectedFilter).some((key) => row[key] === selectedFilter[key]),
            ),
        )
      : [];

    selectedFilters.length > 0 ? setRowsFiltered(rowsMatchingFilters) : setRowsFiltered([]);
  };

  const handleSearch = (searchString: React.SetStateAction<string>, searchKey: keyof T) => {
    const rowsTofilter = rowsFiltered.length > 0 ? rowsFiltered : rows;
    const rowsMatchingFilters = rowsTofilter.filter(
      // TODO - use generic type

      (row: any) => row[searchKey].includes(searchString),
    );
    searchString.length > 0 ? setRowsFiltered(rowsMatchingFilters) : setRowsFiltered(rows);
  };

  if (isLoading) {
    return <SkeletonTableRow id={`skeleton-table-${tableId}`} rowCount={5} cellCount={headCells?.length || 5} />;
  }

  return (
    <>
      <TableContainer style={{ borderTop: 'none' }}>
        <Table size="small" aria-label={ariaLabel} id={tableId}>
          <EnhancedTableHead
            data={rows}
            headCells={headCells}
            order={order}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            orderBy={orderBy}
            handleSort={handleSort}
            handleSearch={handleSearch}
          />
          <TableBody>
            {(!rows || !rows?.length) && (
              <TableRow>
                <TableCell colSpan={headCells.length} className={classes.emptyTable}>
                  <Typography variant="caption"> {emptyMessage} </Typography>
                </TableCell>
              </TableRow>
            )}
            {stableSort(rowsFiltered, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((rowFiltered, index: number) => (
                <DataTableRow
                  key={`${rowFiltered.id}-${index}`}
                  rowId={rowFiltered.id}
                  labelId={`${tableId}-${id}-${index}`}
                  headCells={headCells}
                  row={rowFiltered}
                  renderExpanded={renderExpanded}
                  onRowClick={() => (onRowClick ? onRowClick(rowFiltered.id) : null)}
                  selectedRowId={selectedRowId}
                  borders={borders}
                  url={url}
                />
              ))}
            {showPagination && rowsFiltered?.length > 10 && (
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={rowsFiltered?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Entries per page"
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
